import { TextInput } from '@infotech-soe/erp-ui-kit';
import { AuthMethod } from '@infotech-soe/merps/types/session';
import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import ApplicationSelector from './ApplicationSelector';
import { LoginFormContainerProps } from './AuthFormContainer';
import BaseAuthForm from './BaseAuthForm';
import useLoginFormState from './hooks/useLoginFormState';
import LoginFormField from './LoginFormField';
import { loginFormMessages } from './messages';
import { PasswordInput } from './PasswordInput';

const LoginForm: React.FC<LoginFormContainerProps> = ({
  hasError,
  applications,
  isDarkMode,
  onSubmit,
}) => {
  const {
    state: { values: iState, validation, needChange },
    change,
    toggle,
  } = useLoginFormState({
    login: '',
    pass: '',
    pass1: '',
    pass2: '',
    app: '',
  });

  const { formatMessage } = useIntl();
  const [hasAutofill, setHasAutofill] = useState(false);

  useEffect(() => {
    const checkAutofill = () => {
      const loginInput = document.querySelector<HTMLInputElement>(
        'input[autocomplete="username"]'
      );
      const isAutofilled = loginInput?.matches(':-webkit-autofill') || false;
      setHasAutofill(isAutofilled);
    };

    const timer = setTimeout(checkAutofill, 150);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (iState.login || iState.pass) {
      setHasAutofill(false);
    }
  }, [iState.login, iState.pass]);

  const handleSubmit = useCallback(() => {
    localStorage.setItem('app', iState.app);
    onSubmit({
      type: AuthMethod.LOGIN,
      userName: iState.login,
      password: iState.pass,
      application: iState.app,
      newPassword: iState.pass1,
    });
  }, [iState, onSubmit]);

  const isValid = validation.valid || (hasAutofill && !!iState.app);

  return (
    <BaseAuthForm
      hasError={hasError}
      isDarkMode={isDarkMode}
      logoSrc="/logo.svg"
      needChange={needChange}
      onSubmit={handleSubmit}
      onToggle={toggle}
      valid={isValid}
      textSubtitle={loginFormMessages.loginSubtitle}
    >
      <ApplicationSelector
        applications={applications}
        value={iState.app}
        onChange={change('app')}
      />
      <LoginFormField label={formatMessage(loginFormMessages.login)}>
        <TextInput
          autoComplete="username"
          value={iState.login}
          required
          onChange={change('login')}
        />
      </LoginFormField>
      <PasswordInput
        autoComplete="current-password"
        label={formatMessage(loginFormMessages.password)}
        value={iState.pass}
        required
        onChange={change('pass')}
      />
      {needChange && (
        <div>
          <PasswordInput
            autoComplete="new-password"
            label={formatMessage(loginFormMessages.newPassword)}
            value={iState.pass1}
            required
            onChange={change('pass1')}
          />
          <PasswordInput
            autoComplete="new-password"
            label={formatMessage(loginFormMessages.newPasswordConfirm)}
            value={iState.pass2}
            required
            onChange={change('pass2')}
            invalid={!validation.match}
          />
        </div>
      )}
    </BaseAuthForm>
  );
};

export default LoginForm;
